import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUp, CalendarSearch, Check, Expand, LandPlot, Map, Pencil, Printer, X } from 'lucide-react';
import { IconArrowBarToRightDashed, IconHistory, IconMenu2 } from '@tabler/icons-react';
import {
  useOrderDetailsMockController,
  useOrderDetailsMockPedido,
  useOrderDetailsMockReincidencia,
} from '../hooks/useOrderDetailsMockController';
import { useUserProfilePhotos } from '../../user-management/hooks/useUserProfilePhotos';
import { OrderDetailsDefeitoAudio } from './OrderDetailsDefeitoAudio';
import { OrderDetailsSugestaoModal } from './OrderDetailsSugestaoModal';
import { OrderDetailsEncaminharChamadoModal } from './OrderDetailsEncaminharChamadoModal';
import { OrderDetailsPrevisaoTimer } from './OrderDetailsPrevisaoTimer';
import { OrderDetailsMockHoverTooltip } from './OrderDetailsMockHoverTooltip';
import { OrderDetailsMockCiclos } from './OrderDetailsMockCiclos';
import OpenOsV2EquipmentHistoryOverlay from '../../new-os/components/OpenOsV2EquipmentHistoryOverlay';
import OsReportV2PreviewModal from './os-report-v2/OsReportV2PreviewModal';
import OrderDetailsAddressEditModal from './OrderDetailsAddressEditModal';
import OrderDetailsSolicitanteEditModal from './OrderDetailsSolicitanteEditModal';
import OrderDetailsOryaChat, { OrderDetailsOryaButton } from './OrderDetailsOryaChat';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import useCutoverActive, { defeitoTerm } from '../../pg-native/useCutoverActive';
import './BoardOrderContextMenu.css';

function formatAddressLine2Cep(line2) {
  return String(line2 || '').replace(/\b(\d{5})-?(\d{3})\b/g, '$1-$2');
}

export default function OrderDetailsMockView({
  order,
  onClose,
  statusUptodate = '',
  codstatusRetorno,
  onDadosEquipamento: _onDadosEquipamento,
  canEncaminharChamado = false,
  onEncaminharChamado,
  encaminharPanel = null,
  onEncaminharPanelClose,
  onEncaminharConfirm,
  onMotivoUpdated,
  onTipoUpdated,
  onAddressUpdated,
  onLocalizacaoUpdated,
  onSolicitanteUpdated,
  isAdminUser = false,
  hasCustomersModule = false,
  onReturnToPendenteSuccess,
  isPreAtendimento: isPreAtendimentoProp,
}) {
  const isPreAtendimento = Boolean(isPreAtendimentoProp ?? order?.isPreAtendimento);
  const cutoverActive = useCutoverActive();
  const data = useOrderDetailsMockController(order, {
    onMotivoUpdated,
    onTipoUpdated,
    onAddressUpdated,
    onLocalizacaoUpdated,
    onSolicitanteUpdated,
    isAdminUser,
    hasCustomersModule,
    onReturnToPendenteSuccess,
    codstatusRetorno,
    statusUptodate,
  });

  const [menuOpen, setMenuOpen] = useState(false);
  const [impressao2Open, setImpressao2Open] = useState(false);
  const [motivoExpanded, setMotivoExpanded] = useState(false);
  const [selectedAnexoId, setSelectedAnexoId] = useState(null);
  const [anexoZoomOpen, setAnexoZoomOpen] = useState(false);
  const [railPanel, setRailPanelState] = useState(null);
  const [selectedPedidoId, setSelectedPedidoId] = useState(null);
  const [processoOpen, setProcessoOpen] = useState(false);
  const [originalCycleFocus, setOriginalCycleFocus] = useState(0);
  const [noteInput, setNoteInput] = useState('');
  const [orya, setOrya] = useState(null);
  const [oryaOpen, setOryaOpen] = useState(false);
  const menuRef = useRef(null);
  const defeitoObsRef = useRef(null);
  const [obsIsTruncated, setObsIsTruncated] = useState(false);

  const displayOs = data.displayOs || '';

  useEffect(() => {
    const os = displayOs;
    if (!os) return undefined;
    let cancel = false;
    setOrya(null);
    setOryaOpen(false);
    (async () => {
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/board/orders/${encodeURIComponent(os)}/orya`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
        });
        const json = await res.json();
        if (!cancel && json?.data?.available) setOrya(json.data);
      } catch {
        /* sem manual, o botão não aparece */
      }
    })();
    return () => { cancel = true; };
  }, [displayOs]);
  const tipoOsEdit = data.tipoOsEdit;

  const orderForImpressao2 = useMemo(() => {
    if (!order) return order;
    const nome = data.defeitoNome && data.defeitoNome !== 'Defeito não informado' ? data.defeitoNome : undefined;
    const obs = data.defeitoObservacao ? data.defeitoObservacao : undefined;
    const obsGeral = data.obsGeralText ? data.obsGeralText : undefined;
    if (nome === undefined && obs === undefined && obsGeral === undefined) return order;
    return {
      ...order,
      ...(nome !== undefined ? { defeitoNome: nome } : {}),
      ...(obs !== undefined ? { defeitoObservacao: obs } : {}),
      ...(obsGeral !== undefined ? { observacaoGeral: obsGeral } : {}),
    };
  }, [order, data.defeitoNome, data.defeitoObservacao, data.obsGeralText]);

  const closeMenus = () => {
    setMenuOpen(false);
    tipoOsEdit?.setMenuOpen?.(false);
  };
  const toggleTypeMenu = () => {
    if (!tipoOsEdit?.canEditTipo) return;
    tipoOsEdit.setMenuOpen((v) => !v);
    setMenuOpen(false);
  };
  const toggleMenu = () => {
    setMenuOpen((v) => !v);
    tipoOsEdit?.setMenuOpen?.(false);
  };
  const openImpressao2 = () => {
    closeMenus();
    setImpressao2Open(true);
  };
  const openHistorico = () => {
    setRailPanelState('historico');
    setProcessoOpen(false);
    closeMenus();
    data.openHistoricoView?.();
  };
  const openHistoricoNative = (focusOriginal) => {
    const alreadyOpen = railPanel === 'historico';
    setRailPanelState('historico');
    setProcessoOpen(false);
    closeMenus();
    if (focusOriginal) setOriginalCycleFocus((n) => n + 1);
    if (!alreadyOpen) data.openHistoricoView?.();
  };
  const hasNativeCycles = data.nativeReturnCount > 0;
  const toggleMotivo = () => setMotivoExpanded((v) => !v);
  const startEditMotivo = () => data.handleStartMotivoEdit?.();
  const cancelEditMotivo = () => data.handleCancelMotivoEdit?.();
  const saveEditMotivo = () => data.handleSaveMotivo?.();
  const onMotivoDraftChange = (e) => {
    data.setMotivoEditValue?.(e.target.value.slice(0, data.MOTIVO_OS_MAX || 200));
  };
  const selectAnexo = (id) => {
    setAnexoZoomOpen(false);
    setSelectedAnexoId((cur) => (cur === id ? null : id));
  };

  const setRailPanel = (panel) => {
    setRailPanelState((cur) => (cur === panel ? null : panel));
    setProcessoOpen(false);
  };
  const togglePedido = (id) => {
    if (railPanel === 'pedido' && selectedPedidoId === id) {
      setRailPanelState(null);
      setProcessoOpen(false);
      return;
    }
    setRailPanelState('pedido');
    setSelectedPedidoId(id);
    setProcessoOpen(false);
  };
  const toggleProcesso = () => setProcessoOpen((v) => !v);
  const onNoteInputChange = (e) => setNoteInput(e.target.value);
  const sendNote = async () => {
    const v = noteInput.trim();
    if (!v || data.savingNote) return;
    const ok = await data.submitNote?.(v);
    if (ok) setNoteInput('');
  };
  const closePanel = () => {
    setRailPanelState(null);
    setProcessoOpen(false);
  };
  const setRailPanelNotas = () => setRailPanel('notas');
  const setRailPanelAnexos = () => {
    if (!data.hasCustomersModule) return;
    if (!(data.anexosMediaFiles || []).length) return;
    setRailPanel('anexos');
  };
  const setRailPanelReinc = () => {
    if (!data.isOrderReincidencia) return;
    setRailPanel('reincidencia');
  };
  const setRailPanelTec = () => {
    setRailPanel('tecnicos');
    data.fetchTecnicosTerritorio?.();
  };
  const closeAnexos = () => {
    setAnexoZoomOpen(false);
    setRailPanelState(null);
  };
  const openLogs = () => {
    setRailPanelState('logs');
    closeMenus();
    data.openLogsView?.();
  };
  const closeLogs = () => setRailPanelState(null);
  const openSugestao = () => {
    closeMenus();
    data.setShowSugestaoSolucao?.(true);
  };
  const handleEncaminhar = (e) => {
    closeMenus();
    if (!canEncaminharChamado || !onEncaminharChamado) return;
    const rect = e.currentTarget?.getBoundingClientRect?.();
    onEncaminharChamado(
      rect
        ? {
            x: Math.round(rect.left),
            y: Math.round(rect.top),
            left: rect.left,
            top: rect.top,
            bottom: rect.bottom,
            right: rect.right,
            width: rect.width,
            height: rect.height,
          }
        : null,
    );
  };

  useEffect(() => {
    setRailPanelState(null);
    setSelectedPedidoId(null);
    setProcessoOpen(false);
    setOriginalCycleFocus(0);
    setSelectedAnexoId(null);
    setAnexoZoomOpen(false);
    setNoteInput('');
    setMotivoExpanded(false);
  }, [order?.id]);

  useEffect(() => {
    if (!order?.id) return;
    if (!data.hasNotes && !(Number(data.notesCount) > 0)) return;
    setRailPanelState((cur) => (cur != null && cur !== 'notas' ? cur : 'notas'));
  }, [order?.id, data.hasNotes, data.notesCount]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const isNotasOpen = railPanel === 'notas';
  const isAnexosOpen = railPanel === 'anexos';
  const isLogsOpen = railPanel === 'logs';
  const isContentOpen = railPanel !== 'anexos' && railPanel !== 'logs';
  const isReincOpen = railPanel === 'reincidencia';
  const isTecOpen = railPanel === 'tecnicos';
  const isPedidoOpen = railPanel === 'pedido';
  const isHistoricoOpen = railPanel === 'historico';
  const motivoEditing = Boolean(data.isMotivoEditing);
  const obsGeralEditing = Boolean(data.isObsGeralEditing);
  const motivoPanelEditing = motivoEditing || obsGeralEditing;
  const hasNotes = Boolean(data.hasNotes);
  const notesCount = data.notesCount || 0;
  const notes = data.notesMapped || [];
  const canSend = noteInput.trim().length > 0 && !data.savingNote;
  const sendDisabled = !canSend;

  const defeitoObsText =
    data.defeitoObservacao && data.defeitoObservacao !== '—'
      ? data.defeitoObservacao
      : 'Sem observação do defeito';
  const defeitoObsClamp = !motivoExpanded && !data.isObsEditing;

  useLayoutEffect(() => {
    if (!defeitoObsClamp) {
      setObsIsTruncated(false);
      return undefined;
    }
    const el = defeitoObsRef.current;
    if (!el) return undefined;
    const check = () => {
      setObsIsTruncated(el.scrollHeight > el.clientHeight + 1);
    };
    check();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [defeitoObsClamp, defeitoObsText, order?.id]);

  const motivoMaxHeight = motivoExpanded ? (motivoPanelEditing ? '520px' : '240px') : '0px';
  const motivoOpacity = motivoExpanded ? 1 : 0;
  const motivoMarginTop = motivoExpanded ? '14px' : '0px';
  const motivoToggleLabel = motivoExpanded ? 'Ocultar detalhes' : 'Ver detalhes';

  const panelWidth =
    railPanel && railPanel !== 'anexos' && railPanel !== 'logs'
      ? railPanel === 'pedido'
        ? processoOpen
          ? 720
          : 560
        : 380
      : 0;
  const panelBorder =
    railPanel && railPanel !== 'anexos' && railPanel !== 'logs' ? '1px solid var(--odm-border)' : 'none';

  const processoToggleLabel = processoOpen ? 'Ocultar processo' : 'Ver processo';
  const processoPanelWidth = processoOpen ? '260px' : '0px';
  const processoOpacity = processoOpen ? 1 : 0;
  const processoPanelBorder = processoOpen ? '1px solid var(--odm-border)' : 'none';

  const anexos = useMemo(
    () =>
      (data.anexosMediaFiles || []).map((a, index) => ({
        id: a.url || a.name || String(index),
        name: a.name,
        url: a.url,
        isImage: Boolean(a.isImage),
        ext: a.ext,
        meta: a.ext ? String(a.ext).toUpperCase() : '',
        borderColor: selectedAnexoId === (a.url || a.name || String(index)) ? 'var(--odm-accent)' : 'var(--odm-border)',
      })),
    [data.anexosMediaFiles, selectedAnexoId],
  );
  const notePhotoSubjects = useMemo(
    () =>
      (data.notesMapped || [])
        .map((item) => ({
          id: item?.profilePhotoUserId,
          hasProfilePhoto: item?.hasProfilePhoto === true,
        }))
        .filter((x) => Number(x.id) > 0),
    [data.notesMapped],
  );
  const notePhotoMap = useUserProfilePhotos(notePhotoSubjects, { enabled: isNotasOpen });
  const selectedAnexo = anexos.find((a) => a.id === selectedAnexoId) || null;
  const anexosCount = anexos.length;
  const hasAnexos = anexosCount > 0;
  const logs = data.logsMapped || [];
  const addressLine2Display = formatAddressLine2Cep(data.addressLines?.line2 || '');

  const pedidos = (data.pedidoBlocks || []).map((p) => {
    const active = railPanel === 'pedido' && selectedPedidoId === p.cod;
    return {
      id: p.cod,
      active,
      toggleLabel: active ? 'Fechar' : 'Ver detalhes',
      dotColor: '#DC2626',
    };
  });

  const pedidoLive = useOrderDetailsMockPedido(selectedPedidoId, isPedidoOpen, order?.source === 'native');
  const selectedPedido = {
    id: pedidoLive.codigo || selectedPedidoId || '',
    notaFiscal: pedidoLive.notaFiscal,
    statusAtual: pedidoLive.statusAtual,
    produtos: pedidoLive.produtos || [],
    processo: pedidoLive.processo || [],
    observacoes: pedidoLive.observacoes || '',
    hasProdutos: (pedidoLive.produtos || []).length > 0,
    hasProcesso: (pedidoLive.processo || []).length > 0,
  };

  const reincLive = useOrderDetailsMockReincidencia(
    order?.id,
    Boolean(isReincOpen && data.isOrderReincidencia),
  );
  const reincidencias = reincLive.reincidencias || [];
  const technicians = data.tecnicosTerritorio || [];
  const typeOptions = tipoOsEdit?.changeOptions || [];
  const slaTone = data.slaInfo?.tone || 'ok';
  const slaTitleColor =
    slaTone === 'vencido'
      ? 'var(--odm-sla-title, #B91C1C)'
      : slaTone === 'vencendo'
        ? 'var(--odm-sla-title, #C2410C)'
        : 'var(--odm-sla-title, #15803D)';
  const slaMutedColor =
    slaTone === 'vencido'
      ? 'var(--odm-sla-muted, #9A6B6B)'
      : slaTone === 'vencendo'
        ? 'var(--odm-sla-muted, #B45309)'
        : 'var(--odm-sla-muted, #4D7C59)';
  const slaIconColor = slaTitleColor;
  const slaAbertura = String(data.order?.dataAbertura ?? '').trim() || '—';
  const slaPrevisaoLabel = String(data.previsao ?? data.order?.previsao ?? '').trim() || '—';
  const slaPrefix = String(data.slaMessage || '').replace(/\s*\(Abertura:.*\)$/i, '').trim();

  if (!data.order) return null;

  const defeitoObsSpan = (
    <span
      ref={defeitoObsRef}
      style={{
        fontSize: "13px",
        fontWeight: 400,
        color: "var(--odm-muted)",
        lineHeight: 1.35,
        width: "100%",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        ...(defeitoObsClamp
          ? {
              display: "-webkit-box",
              WebkitLineClamp: 4,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              whiteSpace: "normal",
            }
          : null),
      }}
    >
      {defeitoObsText}
    </span>
  );
  const defeitoObsNode =
    defeitoObsClamp && obsIsTruncated ? (
      <OrderDetailsMockHoverTooltip
        text={defeitoObsText}
        className="order-details-mock-tip-wrap--block order-details-mock-obs-tooltip"
        placement="above"
        selectable
        wrap
      >
        {defeitoObsSpan}
      </OrderDetailsMockHoverTooltip>
    ) : (
      <span style={{flex: 1, minWidth: 0}}>{defeitoObsSpan}</span>
    );

  const shellIsWide = Boolean(panelWidth);
  const shellWidth = shellIsWide ? '96vw' : '90vw';
  const shellMaxWidth = shellIsWide ? '1240px' : '1080px';

  return (
    <>
<div className="order-details-mock-card" style={{position: "relative", fontFamily: "'Geist Variable', Geist, sans-serif", color: "var(--odm-text)", width: shellWidth, maxWidth: shellMaxWidth, height: "min(620px, calc(100vh - 48px))", background: "var(--odm-bg)", borderRadius: "16px", border: "1px solid var(--odm-border)", boxShadow: "var(--odm-shadow)", display: "flex", flexDirection: "column", overflow: "hidden", transition: "width .22s ease, max-width .22s ease"}}>

  {/* HEADER */}
  <div style={{padding: "14px 17px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px"}}>
    <div style={{display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap"}}>
      <span style={{fontSize: "16px", fontWeight: 800, letterSpacing: "-0.01em", color: "var(--odm-title)", marginRight: "2px"}}>{`OS ${displayOs}`}</span>
      {data.nativeCycle > 0 ? (
        <OrderDetailsMockHoverTooltip text="Ver ciclos no Histórico do processo" placement="below">
          <button type="button" onClick={() => openHistoricoNative(false)} style={{display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", fontWeight: 700, height: "28px", boxSizing: "border-box", padding: "0 9px", borderRadius: "7px", cursor: "pointer", border: "1px solid var(--odm-border)", background: "var(--odm-return-bg)", color: "var(--odm-return-text)", boxShadow: "0 1px 2px rgba(0,0,0,0.03)"}}>
            Ciclo {data.nativeCycle}
          </button>
        </OrderDetailsMockHoverTooltip>
      ) : null}
      {data.showStandbyNotesIndicator ? (
        <OrderDetailsMockHoverTooltip text="Informações sobre agendamento futuro" placement="below">
          <span ref={data.standbyNotesAnchorRef} style={{position: "relative", display: "inline-flex"}}>
            <button
              type="button"
              aria-expanded={data.standbyNotesPopoverOpen}
              onClick={() => data.setStandbyNotesPopoverOpen((v) => !v)}
              style={{width: "22px", height: "22px", border: "1px solid var(--odm-border)", background: "var(--odm-chip-bg)", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--odm-icon)"}}
            >
              <CalendarSearch size={12} strokeWidth={2.25} aria-hidden />
            </button>
            {data.standbyNotesPopoverOpen ? (
              <span role="tooltip" style={{position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 40, minWidth: "220px", background: "var(--odm-chip-bg)", border: "1px solid var(--odm-border)", borderRadius: "8px", padding: "8px 10px", fontSize: "12px", fontWeight: 500, color: "var(--odm-text-secondary)", boxShadow: "0 8px 20px rgba(20,20,43,0.12)"}}>
                {data.standbyNotesTooltipText}
              </span>
            ) : null}
          </span>
        </OrderDetailsMockHoverTooltip>
      ) : null}

      <div ref={tipoOsEdit?.menuRef} style={{position: "relative", display: "flex", alignItems: "center", gap: "2px", height: "28px", boxSizing: "border-box", background: "var(--odm-chip-bg)", border: "1px solid var(--odm-border)", padding: isPreAtendimento ? "0 10px" : "0 4px 0 10px", borderRadius: "7px", boxShadow: "0 1px 2px rgba(0,0,0,0.03)"}}>
        <span style={{fontSize: "11px", fontWeight: 600, color: "var(--odm-ink)"}}>{tipoOsEdit?.tipoLabel || '—'}</span>
        {tipoOsEdit?.canEditTipo && !isPreAtendimento ? (
          <OrderDetailsMockHoverTooltip text="Alterar tipo de OS" placement="below">
            <button type="button" onClick={toggleTypeMenu} disabled={tipoOsEdit.isSaving} style={{width: "18px", height: "18px", border: "none", background: "transparent", color: "var(--odm-icon)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "5px", cursor: "pointer"}} aria-label="Alterar tipo de OS">
              <Pencil size={14} strokeWidth={2} aria-hidden />
            </button>
          </OrderDetailsMockHoverTooltip>
        ) : null}
        {tipoOsEdit?.menuOpen && !isPreAtendimento ? (
          <div className="context-menu board-order-context-menu" role="menu" style={{position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 30}}>
            <div className="board-order-context-menu-label">Tipo de OS</div>
            <div className="board-order-context-menu-separator" role="separator" />
            {typeOptions.map((opt) => (
              <button key={opt.code} type="button" role="menuitem" className="board-order-context-menu-item" disabled={tipoOsEdit.isSaving} onClick={() => tipoOsEdit.handleSelectTipo(opt.code)}>
                <span className="board-order-context-menu-item-label">{opt.label}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <span style={{display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", fontWeight: 600, color: "var(--odm-ink)", background: "var(--odm-chip-bg)", border: "1px solid var(--odm-border)", height: "28px", boxSizing: "border-box", padding: "0 9px", borderRadius: "7px", boxShadow: "0 1px 2px rgba(0,0,0,0.03)"}}>
        {data.statusName}
        {data.isRetorno ? <span style={{fontSize: "10px", fontWeight: 700, color: "var(--odm-muted)"}}>Retorno</span> : null}
        <span aria-hidden style={{display: "inline-block", width: "4px", height: "4px", margin: "0 1px", borderRadius: "50%", background: "var(--odm-dot)", flexShrink: 0}} />
        <OrderDetailsMockHoverTooltip text="Histórico do processo" placement="below">
          <button
            type="button"
            onClick={hasNativeCycles ? () => openHistoricoNative(true) : openHistorico}
            aria-label="Histórico do processo"
            style={{display: "inline-flex", alignItems: "center", justifyContent: "center", width: "18px", height: "18px", padding: 0, border: "none", background: "transparent", color: "var(--odm-icon)", borderRadius: "5px", cursor: "pointer"}}
          >
            <IconHistory size={14} stroke={1.8} aria-hidden />
          </button>
        </OrderDetailsMockHoverTooltip>
      </span>

      {!isPreAtendimento ? (
        data.isOrderReincidencia ? (
          <button type="button" onClick={setRailPanelReinc} style={{display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", fontWeight: 600, height: "28px", boxSizing: "border-box", padding: "0 9px", borderRadius: "7px", cursor: "pointer", border: "1px solid var(--odm-border)", background: "var(--odm-chip-bg)", color: "var(--odm-ink)", boxShadow: "0 1px 2px rgba(0,0,0,0.03)"}}>
            Reincidência: Sim
          </button>
        ) : (
          <span style={{display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", fontWeight: 600, height: "28px", boxSizing: "border-box", padding: "0 9px", borderRadius: "7px", border: "1px solid var(--odm-border)", background: "var(--odm-chip-bg)", color: "var(--odm-ink)", boxShadow: "0 1px 2px rgba(0,0,0,0.03)"}}>
            Reincidência: Não
          </span>
        )
      ) : null}
    </div>

    <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
      {orya ? (
        <span className="odm-orya-anchor">
          <OrderDetailsOryaButton open={oryaOpen} onClick={() => setOryaOpen((v) => !v)} />
          {oryaOpen ? (
            <OrderDetailsOryaChat
              os={displayOs}
              ready={Boolean(orya.ready)}
              onClose={() => setOryaOpen(false)}
            />
          ) : null}
        </span>
      ) : null}
      <button type="button" onClick={setRailPanelNotas} style={{position: "relative", display: "flex", alignItems: "center", gap: "5px", height: "28px", boxSizing: "border-box", padding: "0 10px", borderRadius: "7px", fontSize: "11px", fontWeight: 600, cursor: "pointer", border: "1px solid var(--odm-border)", background: "var(--odm-chip-bg)", color: "var(--odm-ink)", boxShadow: "0 1px 2px rgba(0,0,0,0.03)"}}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
        Notas
        {hasNotes ? (

          <span style={{minWidth: "15px", height: "15px", padding: "0 3px", borderRadius: "8px", background: "var(--odm-ink)", color: "var(--odm-on-ink)", fontSize: "9px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center"}}>{notesCount}</span>
        
) : null}
      </button>

      {data.hasCustomersModule ? (
      <OrderDetailsMockHoverTooltip text={hasAnexos ? `Anexos (${anexosCount})` : 'Sem anexos'} placement="below">
      <button
        type="button"
        onClick={setRailPanelAnexos}
        disabled={!hasAnexos}
        aria-label={hasAnexos ? `Anexos (${anexosCount})` : 'Anexos'}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "5px",
          height: "28px",
          boxSizing: "border-box",
          padding: "0 8px",
          borderRadius: "7px",
          fontSize: "11px",
          fontWeight: 600,
          cursor: hasAnexos ? "pointer" : "not-allowed",
          border: "1px solid var(--odm-border)",
          background: "var(--odm-chip-bg)",
          color: "var(--odm-ink)",
          boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
          opacity: hasAnexos ? 1 : 0.55,
        }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>
        {hasAnexos ? (
          <span style={{minWidth: "15px", height: "15px", padding: "0 3px", borderRadius: "8px", background: "var(--odm-ink)", color: "var(--odm-on-ink)", fontSize: "9px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center"}}>{anexosCount}</span>
        ) : null}
      </button>
      </OrderDetailsMockHoverTooltip>
      ) : null}
      <div style={{width: "1px", height: "20px", background: "var(--odm-border)", margin: "0 2px"}}></div>

      <OrderDetailsMockHoverTooltip text="Impressão" placement="below">
        <button
          type="button"
          onClick={openImpressao2}
          style={{display: "flex", alignItems: "center", justifyContent: "center", width: "28px", height: "28px", color: "var(--odm-ink)", background: "var(--odm-chip-bg)", boxSizing: "border-box", borderRadius: "7px", border: "1px solid var(--odm-border)", cursor: "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.03)"}}
          aria-label="Impressão"
        >
          <Printer size={14} strokeWidth={2.25} aria-hidden />
        </button>
      </OrderDetailsMockHoverTooltip>

      {!isPreAtendimento ? (
        <div ref={menuRef} style={{position: "relative"}}>
          <OrderDetailsMockHoverTooltip text="Menu" placement="below">
            <button
              type="button"
              onClick={toggleMenu}
              style={{display: "flex", alignItems: "center", justifyContent: "center", width: "28px", height: "28px", color: "var(--odm-ink)", background: "var(--odm-chip-bg)", boxSizing: "border-box", borderRadius: "7px", border: "1px solid var(--odm-border)", cursor: "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.03)"}}
              aria-label="Menu"
              aria-expanded={menuOpen}
            >
              <IconMenu2 size={15} stroke={1.8} aria-hidden />
            </button>
          </OrderDetailsMockHoverTooltip>
          {menuOpen ? (
            <div className="context-menu board-order-context-menu" role="menu" style={{position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 30}}>
              <div className="board-order-context-menu-label">Ações</div>
              <div className="board-order-context-menu-separator" role="separator" />
              {data.canReturnToPendente ? (
                <button type="button" role="menuitem" className="board-order-context-menu-item" disabled={data.returnToPendenteBusy} onClick={() => { closeMenus(); data.handleReturnToPendente?.(); }}>
                  <span className="board-order-context-menu-item-label">
                    <i className="bi bi-arrow-counterclockwise board-order-context-menu-item-icon" aria-hidden />
                    {data.returnToPendenteBusy ? 'Retornando…' : 'Retornar para pendência'}
                  </span>
                </button>
              ) : null}
              {canEncaminharChamado && onEncaminharChamado ? (
                <button type="button" role="menuitem" className="board-order-context-menu-item" onClick={handleEncaminhar}>
                  <span className="board-order-context-menu-item-label">
                    <i className="bi bi-forward board-order-context-menu-item-icon" aria-hidden />
                    Encaminhar chamado
                  </span>
                </button>
              ) : null}
              <button type="button" role="menuitem" className="board-order-context-menu-item" onClick={openLogs}>
                <span className="board-order-context-menu-item-label">
                  <i className="bi bi-clock-history board-order-context-menu-item-icon" aria-hidden />
                  Logs
                </span>
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      <OrderDetailsMockHoverTooltip text="Fechar" placement="below">
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          style={{display: "flex", alignItems: "center", justifyContent: "center", width: "28px", height: "28px", color: "var(--odm-ink)", background: "transparent", boxSizing: "border-box", borderRadius: "7px", border: "none", cursor: "pointer"}}
        >
          <X size={15} strokeWidth={2} aria-hidden />
        </button>
      </OrderDetailsMockHoverTooltip>
    </div>
  </div>

  {data.returnToPendenteBusy || data.returnToPendenteFeedback ? (
    <div
      className={`order-details-mock-return-banner${
        data.returnToPendenteFeedback
          ? ` order-details-mock-return-banner--${data.returnToPendenteFeedback.type}`
          : ' order-details-mock-return-banner--busy'
      }`}
      role={data.returnToPendenteFeedback?.type === 'error' ? 'alert' : 'status'}
    >
      {data.returnToPendenteBusy ? 'Retornando OS para pendência…' : data.returnToPendenteFeedback.message}
    </div>
  ) : null}

  {/* SLA STRIP */}
  {!isPreAtendimento ? (
    <div className={`order-details-mock-sla--${slaTone}`} style={{padding: "8px 17px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "14px", flexWrap: "wrap", fontSize: "12px"}}>
      <div style={{display: "flex", alignItems: "center", gap: "8px", minWidth: 0}}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={slaIconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><path d="M12 9v4"></path><path d="M12 17h.01"></path></svg>
        <span style={{color: slaTitleColor, minWidth: 0}}>
          <span style={{fontWeight: 700}}>{slaPrefix}</span>
          <span style={{fontWeight: 400}}>{` (abertura: ${slaAbertura} - previsão: ${slaPrevisaoLabel})`}</span>
        </span>
      </div>
      <div style={{display: "flex", alignItems: "center", gap: "10px", color: slaMutedColor}}>
        {data.previsao ? <OrderDetailsPrevisaoTimer previsaoStr={data.previsao} variant="slaBanner" /> : null}
      </div>
    </div>
  ) : null}

  {/* BODY */}
  <div style={{display: "flex", flex: 1, minHeight: 0}}>
    <div style={{flex: 1, minWidth: 0, height: "100%", overflowX: "hidden", overflowY: "auto"}}>
    {isAnexosOpen ? (

      <div style={{display: "flex", flexDirection: "column", height: "100%"}}>
        <div style={{padding: "14px 18px 0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px"}}>
          <span style={{fontSize: "13px", fontWeight: 700, color: "var(--odm-text)"}}>Anexos</span>
          <button type="button" onClick={closeAnexos} style={{width: "26px", height: "26px", border: "none", background: "transparent", color: "var(--odm-muted)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0}} aria-label="Fechar">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"></path><path d="M6 6l12 12"></path></svg>
          </button>
        </div>
        <div style={{flex: 1, display: "flex", overflow: "hidden", minHeight: 0}}>
          <div style={{width: "220px", flexShrink: 0, overflowY: "auto", padding: "12px 14px 14px", display: "flex", flexDirection: "column", gap: "10px", borderRight: "1px solid var(--odm-border)"}}>
            {data.anexosLoading ? <div style={{fontSize: "12px", color: "var(--odm-muted)"}}>Carregando anexos…</div> : null}
            {data.anexosError ? <div style={{fontSize: "12px", color: "#DC2626"}}>{data.anexosError}</div> : null}
            {!data.anexosLoading && !data.anexosError && anexos.length === 0 ? <div style={{fontSize: "12px", color: "var(--odm-muted)"}}>Nenhum anexo nesta OS.</div> : null}
            {anexos.map((a) => (

              <div key={a.id} onClick={() => selectAnexo(a.id)} style={{ border: `1px solid ${a.borderColor}`, borderRadius: "8px", overflow: "hidden", cursor: "pointer", background: "var(--odm-surface-soft)" }}>
                <div style={{height: "90px", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--odm-muted)", background: "var(--odm-surface-alt)", overflow: "hidden"}}>
                  {a.isImage && a.url ? (
                    <img src={a.url} alt="" style={{width: "100%", height: "100%", objectFit: "cover"}} />
                  ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"></rect><circle cx="9" cy="9" r="2"></circle><path d="m21 15-3.5-3.5a2 2 0 0 0-2.8 0L5 21"></path></svg>
                  )}
                </div>
                <div style={{fontSize: "12px", fontWeight: 600, color: "var(--odm-text)", padding: "6px 8px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{a.name}</div>
              </div>
            
))}
          </div>
          {selectedAnexo ? (

            <div style={{flex: 1, minWidth: 0, overflowY: "auto", padding: "14px 18px 18px", display: "flex", flexDirection: "column"}}>
              <div className="order-details-mock-anexo-preview">
                {selectedAnexo.isImage && selectedAnexo.url ? (
                  <>
                    <img src={selectedAnexo.url} alt={selectedAnexo.name} className="order-details-mock-anexo-preview-img" />
                    <OrderDetailsMockHoverTooltip text="Ampliar foto" placement="above">
                      <button
                        type="button"
                        className="order-details-mock-anexo-zoom-btn"
                        aria-label="Ampliar foto"
                        onClick={() => setAnexoZoomOpen(true)}
                      >
                        <Expand size={14} strokeWidth={2} aria-hidden />
                      </button>
                    </OrderDetailsMockHoverTooltip>
                  </>
                ) : String(selectedAnexo.ext || '').toLowerCase() === 'pdf' && selectedAnexo.url ? (
                  <iframe title={selectedAnexo.name} src={selectedAnexo.url} style={{width: "100%", height: "100%", border: "none"}} />
                ) : (
                  <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", color: "var(--odm-muted)"}}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"></rect><circle cx="9" cy="9" r="2"></circle><path d="m21 15-3.5-3.5a2 2 0 0 0-2.8 0L5 21"></path></svg>
                    {selectedAnexo.url ? <a href={selectedAnexo.url} target="_blank" rel="noreferrer" style={{fontSize: "12px", fontWeight: 600}}>Abrir arquivo</a> : null}
                  </div>
                )}
              </div>
              <div style={{marginTop: "10px", fontSize: "13px", fontWeight: 600, color: "var(--odm-text)"}}>{selectedAnexo.name}</div>
              <div style={{fontSize: "11px", color: "var(--odm-muted)", marginTop: "2px"}}>{selectedAnexo.meta}</div>
            </div>
          
) : null}
        </div>
      </div>
    
) : null}
    {isLogsOpen ? (

      <div style={{display: "flex", flexDirection: "column", height: "100%"}}>
        <div style={{padding: "14px 18px", borderBottom: "1px solid var(--odm-border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
          <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 3"></path></svg>
            <span style={{fontSize: "14px", fontWeight: 700, color: "var(--odm-title)"}}>Logs</span>
            <span style={{fontSize: "12px", color: "var(--odm-muted)", fontFamily: "'Geist Mono',monospace"}}>{`OS ${displayOs}`}</span>
          </div>
          <button type="button" onClick={closeLogs} style={{width: "26px", height: "26px", border: "none", background: "transparent", color: "var(--odm-muted)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer"}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"></path><path d="M6 6l12 12"></path></svg>
          </button>
        </div>
        <div style={{flex: 1, overflow: "auto", padding: "0 18px", position: "relative"}}>
          <table style={{width: "100%", minWidth: "600px", borderCollapse: "collapse", fontSize: "10px", tableLayout: "auto"}}>
            <thead>
              <tr style={{position: "sticky", top: 0, background: "var(--odm-chip-bg)"}}>
                <th style={{textAlign: "left", padding: "10px 4px 8px 0", color: "var(--odm-muted)", fontWeight: 600, fontSize: "9.5px", textTransform: "uppercase", letterSpacing: "0.02em", borderBottom: "1px solid var(--odm-border)"}}>#</th>
                <th style={{textAlign: "left", padding: "10px 4px 8px 0", color: "var(--odm-muted)", fontWeight: 600, fontSize: "9.5px", textTransform: "uppercase", letterSpacing: "0.02em", borderBottom: "1px solid var(--odm-border)"}}>Data e hora</th>
                <th style={{textAlign: "left", padding: "10px 4px 8px 0", color: "var(--odm-muted)", fontWeight: 600, fontSize: "9.5px", textTransform: "uppercase", letterSpacing: "0.02em", borderBottom: "1px solid var(--odm-border)"}}>Usuário</th>
                <th style={{textAlign: "left", padding: "10px 4px 8px 0", color: "var(--odm-muted)", fontWeight: 600, fontSize: "9.5px", textTransform: "uppercase", letterSpacing: "0.02em", borderBottom: "1px solid var(--odm-border)"}}>Origem</th>
                <th style={{textAlign: "left", padding: "10px 4px 8px 0", color: "var(--odm-muted)", fontWeight: 600, fontSize: "9.5px", textTransform: "uppercase", letterSpacing: "0.02em", borderBottom: "1px solid var(--odm-border)"}}>Destino</th>
                <th style={{textAlign: "left", padding: "10px 4px 8px 0", color: "var(--odm-muted)", fontWeight: 600, fontSize: "9.5px", textTransform: "uppercase", letterSpacing: "0.02em", borderBottom: "1px solid var(--odm-border)"}}>Técnico atual</th>
                <th style={{textAlign: "left", padding: "10px 4px 8px 0", color: "var(--odm-muted)", fontWeight: 600, fontSize: "9.5px", textTransform: "uppercase", letterSpacing: "0.02em", borderBottom: "1px solid var(--odm-border)"}}>Técnico novo</th>
                <th style={{textAlign: "left", padding: "10px 4px 8px 0", color: "var(--odm-muted)", fontWeight: 600, fontSize: "9.5px", textTransform: "uppercase", letterSpacing: "0.02em", borderBottom: "1px solid var(--odm-border)"}}>Sessão (origem)</th>
                <th style={{textAlign: "left", padding: "10px 0 8px 0", color: "var(--odm-muted)", fontWeight: 600, fontSize: "9.5px", textTransform: "uppercase", letterSpacing: "0.02em", borderBottom: "1px solid var(--odm-border)"}}>Sessão (destino)</th>
              </tr>
            </thead>
            <tbody>
              {data.loadingAuditLogs ? (
                <tr><td colSpan={9} style={{padding: "16px 0", color: "var(--odm-muted)"}}>Carregando logs…</td></tr>
              ) : data.auditLogsError ? (
                <tr><td colSpan={9} style={{padding: "16px 0", color: "#DC2626"}}>{data.auditLogsError}</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={9} style={{padding: "16px 0", color: "var(--odm-muted)"}}>Nenhum log de movimentação para esta OS.</td></tr>
              ) : logs.map((log) => (

                <tr key={log.n} style={{borderBottom: "1px solid var(--odm-surface-alt)"}}>
                  <td style={{padding: "7px 4px 7px 0", color: "var(--odm-muted)"}}>{log.n}</td>
                  <td style={{padding: "7px 4px 7px 0", color: "var(--odm-text)", fontWeight: 600, whiteSpace: "nowrap"}}>{log.datetime}</td>
                  <td style={{padding: "7px 4px 7px 0", color: "var(--odm-text)"}}>{log.usuario}</td>
                  <td style={{padding: "7px 4px 7px 0", color: "var(--odm-text)"}}>{log.origem}</td>
                  <td style={{padding: "7px 4px 7px 0", color: "var(--odm-text)"}}>{log.destino}</td>
                  <td style={{padding: "7px 4px 7px 0", color: "var(--odm-text)"}}>{log.tecAtual}</td>
                  <td style={{padding: "7px 4px 7px 0", color: "var(--odm-text)"}}>{log.tecNovo}</td>
                  <td style={{padding: "7px 4px 7px 0", color: "var(--odm-text)"}}>{log.sessaoOrigem}</td>
                  <td style={{padding: "7px 0 7px 0", color: "var(--odm-text)"}}>{log.sessaoDestino}</td>
                </tr>
              
))}
            </tbody>
          </table>
        </div>
      </div>
    
) : null}
    {isContentOpen ? (
      <div style={{padding: isPedidoOpen ? "18px 16px 18px 17px" : "18px 17px", display: "grid", gridTemplateColumns: isPedidoOpen ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)", gap: "14px", alignContent: "start", alignItems: "start", width: "100%", minWidth: 0, boxSizing: "border-box"}}>

      {/* COL 1 */}
      <div style={{display: "flex", flexDirection: "column", gap: "12px", minWidth: 0, maxWidth: isPedidoOpen ? "320px" : undefined}}>

        <div style={{background: "var(--odm-chip-bg)", border: "1px solid var(--odm-border)", borderRadius: "10px", padding: "14px 16px"}}>
          <div style={{display: "flex", alignItems: "center", gap: "7px", marginBottom: "12px"}}>
            <div style={{width: "20px", height: "20px", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--odm-title)", flexShrink: 0}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4"></path><path d="M12 8h.01"></path></svg>
            </div>
            <span style={{fontSize: "13px", fontWeight: 700, color: "var(--odm-title)"}}>Detalhes do Atendimento</span>
          </div>

          <div style={{marginBottom: "10px"}}>
            <div style={{fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--odm-muted)", marginBottom: "3px"}}>Cliente</div>
            <div style={{fontSize: "13px", fontWeight: 600, color: "var(--odm-text)"}}>{data.cliente}</div>
          </div>
          <div style={{marginBottom: "10px"}}>
            <div style={{fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--odm-muted)", marginBottom: "3px"}}>Contrato</div>
            <div style={{fontSize: "13px", fontWeight: 600, color: "var(--odm-text)"}}>{data.contrato}</div>
          </div>
          <div>
            <div style={{fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--odm-muted)", marginBottom: "3px"}}>Equipamento</div>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-end"}}>
              <div>
                <div style={{fontSize: "13px", fontWeight: 700, fontFamily: "'Geist Mono',monospace", color: "var(--odm-text)"}}>{data.equipamento}</div>
                <div style={{display: "flex", alignItems: "center", gap: "8px", marginTop: "1px"}}>
                  <span style={{display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "11px", fontFamily: "'Geist Mono',monospace", color: "var(--odm-muted)"}}>
                    <OrderDetailsMockHoverTooltip text="Número de série" placement="above" selectable>
                      <span>{data.numeroSerie || '—'}</span>
                    </OrderDetailsMockHoverTooltip>
                    <span aria-hidden>·</span>
                    <OrderDetailsMockHoverTooltip text="Patrimônio/tag" placement="above" selectable>
                      <span>{data.patrimonio || '—'}</span>
                    </OrderDetailsMockHoverTooltip>
                  </span>
                  <button type="button" onClick={data.handleHistoricoServicoClick} style={{display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: 600, color: "var(--odm-text-secondary)", background: "var(--odm-surface-alt)", border: "none", borderRadius: "6px", padding: "4px 8px", cursor: "pointer", whiteSpace: "nowrap"}}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 3"></path></svg>
                    Histórico de serviço
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{background: "var(--odm-chip-bg)", border: "1px solid var(--odm-border)", borderRadius: "10px", padding: "14px 16px"}}>
          <div style={{display: "flex", alignItems: "center", gap: "7px", marginBottom: "12px"}}>
            <div style={{width: "20px", height: "20px", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--odm-title)", flexShrink: 0}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94z"></path></svg>
            </div>
            <span style={{fontSize: "13px", fontWeight: 700, color: "var(--odm-title)"}}>Informações Técnicas</span>
          </div>

          <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px"}}>
            <div>
              <div style={{fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--odm-muted)", marginBottom: "3px"}}>Técnico</div>
              <div style={{fontSize: "13px", fontWeight: 600, color: "var(--odm-text)"}}>{data.tecnico}</div>
            </div>
            <div>
              <div style={{fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--odm-muted)", marginBottom: "3px"}}>Técnico Máquina</div>
              <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
                <span style={{fontSize: "13px", fontWeight: 600, color: "var(--odm-text)"}}>{data.tecmaq}</span>
                {data.isTecnicoTerritorio ? (
                <button type="button" onClick={setRailPanelTec} style={{display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: 600, color: "var(--odm-text-secondary)", background: "var(--odm-surface-alt)", border: "none", borderRadius: "6px", padding: "4px 8px", cursor: "pointer", whiteSpace: "nowrap"}}>
                  <LandPlot size={12} strokeWidth={2} aria-hidden />
                  Território
                </button>
                ) : null}
              </div>
            </div>
          </div>

          {(data.loadingLinkedPedidos || pedidos.length > 0) ? (
          <div style={{borderTop: "1px solid var(--odm-border)", paddingTop: "10px"}}>
            <div style={{fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--odm-muted)", marginBottom: "6px"}}>Pedidos Vinculados</div>

            {data.loadingLinkedPedidos ? <div style={{fontSize: "12px", color: "var(--odm-muted)"}}>Carregando pedidos…</div> : null}
            {pedidos.map((p) => (

              <div key={p.id} style={{padding: "7px 0"}}>
                <div style={{display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap"}}>
                  <span style={{fontSize: "12px", fontWeight: 700, fontFamily: "'Geist Mono',monospace", color: "var(--odm-text)"}}>{p.id}</span>
                  <span style={{width: "7px", height: "7px", borderRadius: "50%", background: p.dotColor, display: "inline-block"}}></span>
                  <button type="button" onClick={() => togglePedido(p.id)} style={{display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: 600, color: "var(--odm-text-secondary)", background: "var(--odm-surface-alt)", border: "none", borderRadius: "6px", padding: "4px 8px", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0}}>
                    {p.toggleLabel}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6"></path></svg>
                  </button>
                </div>
              </div>
            
))}
          </div>
          ) : null}
        </div>
      </div>

      {/* COL 2 — oculta com pedido aberto para dar espaço ao detalhe */}
      {!isPedidoOpen ? (
      <div style={{display: "flex", flexDirection: "column", gap: "12px", minWidth: 0}}>

        <div style={{background: "var(--odm-chip-bg)", border: "1px solid var(--odm-border)", borderRadius: "10px", padding: "14px 16px", minWidth: 0, overflow: "hidden"}}>
          <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px"}}>
            <div style={{display: "flex", alignItems: "center", gap: "7px"}}>
              <div style={{width: "20px", height: "20px", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--odm-title)", flexShrink: 0}}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><path d="M12 9v4"></path><path d="M12 17h.01"></path></svg>
              </div>
              <span style={{fontSize: "13px", fontWeight: 700, color: "var(--odm-title)"}}>Motivo do Chamado</span>
            </div>
            {data.hasAlert ? (
              <OrderDetailsMockHoverTooltip text="Código de alerta do defeito" placement="below">
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    padding: "3px 7px",
                    borderRadius: "5px",
                    background: data.alertHex || "var(--odm-accent)",
                    color: data.alertTextColor || "var(--odm-chip-bg)",
                    flexShrink: 0,
                  }}
                >
                  {data.alertCode}
                </span>
              </OrderDetailsMockHoverTooltip>
            ) : null}
          </div>

          <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px"}}>
            <div style={{flex: 1, minWidth: 0}}>
              <div style={{display: "flex", alignItems: "flex-start", gap: "8px", marginTop: "6px", flexWrap: "wrap"}}>
                <span style={{fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--odm-muted)", flexShrink: 0, marginTop: "2px"}}>{defeitoTerm(cutoverActive)}</span>
                {data.isDefeitoEditing ? (
                  <div className="odm-defeito-edit">
                    <div className="odm-defeito-edit-row">
                      <div className="odm-defeito-field-wrap">
                        {data.selectedDefeito?.codigo ? (
                          <div className="odm-defeito-chip" title={data.selectedDefeito.nome}>
                            <span className="odm-defeito-chip-label">{data.selectedDefeito.nome}</span>
                            <button
                              type="button"
                              className="odm-defeito-chip-clear"
                              onClick={() => data.handleClearSelectedDefeito?.()}
                              aria-label="Remover defeito selecionado"
                              disabled={data.isSavingDefeitoObs}
                            >
                              <X size={12} strokeWidth={2.5} aria-hidden />
                            </button>
                          </div>
                        ) : (
                          <input
                            type="text"
                            className="odm-defeito-input"
                            value={data.defeitoSearchQuery || ''}
                            onChange={(e) => data.handleDefeitoSearchChange?.(e)}
                            placeholder="Digite 2+ caracteres para buscar..."
                            aria-label="Buscar defeito"
                            autoFocus
                            disabled={data.isSavingDefeitoObs}
                          />
                        )}
                        {!data.selectedDefeito?.codigo && data.isDefeitoSearchLoading ? (
                          <div className="odm-defeito-hint">Buscando…</div>
                        ) : null}
                        {!data.selectedDefeito?.codigo && data.defeitoSearchError ? (
                          <div className="odm-defeito-hint odm-defeito-hint--error">{data.defeitoSearchError}</div>
                        ) : null}
                        {!data.selectedDefeito?.codigo && (data.defeitoSearchResults || []).length > 0 ? (
                          <div className="odm-defeito-results" role="listbox" aria-label="Resultados de defeito">
                            {(data.defeitoSearchResults || []).map((item) => (
                              <button
                                key={item.codigo || item.nome}
                                type="button"
                                role="option"
                                className="odm-defeito-result-row"
                                onClick={() => data.handleSelectDefeitoResult?.(item)}
                              >
                                <span className="odm-defeito-result-name">{item.nome}</span>
                                {(item.tipoBadges || []).length > 0 ? (
                                  <span className="odm-defeito-tipo-badges">
                                    {item.tipoBadges.map((b) => (
                                      <span key={b.sigla} className="odm-defeito-tipo-badge" title={b.label}>
                                        {b.sigla}
                                      </span>
                                    ))}
                                  </span>
                                ) : null}
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                      <div className="odm-defeito-actions">
                        <button
                          type="button"
                          className="odm-defeito-action-btn odm-defeito-action-btn--check"
                          onClick={() => data.handleSaveDefeitoObs?.()}
                          disabled={data.isSavingDefeitoObs || !data.selectedDefeito?.codigo}
                          title="Salvar defeito"
                          aria-label="Salvar defeito"
                        >
                          <Check size={14} strokeWidth={2} aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="odm-defeito-action-btn odm-defeito-action-btn--cancel"
                          onClick={() => data.handleCancelDefeitoObsEdit?.()}
                          disabled={data.isSavingDefeitoObs}
                          title="Cancelar"
                          aria-label="Cancelar edição do defeito"
                        >
                          <X size={14} strokeWidth={2} aria-hidden />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{display: "inline-flex", alignItems: "center", gap: "6px", minWidth: 0, flex: 1}}>
                    <span style={{fontSize: "13px", fontWeight: 700, color: "var(--odm-text)", minWidth: 0, overflowWrap: "anywhere", wordBreak: "break-word"}}>{data.defeitoNome}</span>
                    <OrderDetailsMockHoverTooltip text="Editar defeito" placement="above">
                      <button
                        type="button"
                        onClick={() => data.handleStartDefeitoEdit?.()}
                        style={{width: "18px", height: "18px", border: "none", background: "transparent", color: "var(--odm-icon)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "5px", cursor: "pointer", flexShrink: 0}}
                        aria-label="Editar defeito"
                      >
                        <Pencil size={14} strokeWidth={2} aria-hidden />
                      </button>
                    </OrderDetailsMockHoverTooltip>
                    {data.hasCustomersModule && data.defeitoAudioFile?.url ? (
                      <span style={{display: "inline-flex", alignItems: "center", flexShrink: 0}}>
                        <OrderDetailsDefeitoAudio
                          src={data.defeitoAudioFile.url}
                          orderId={order?.id || order?.TB02115_CODIGO}
                          onTranscribed={data.onAudioTranscribed}
                        />
                      </span>
                    ) : null}
                  </div>
                )}
              </div>

              {data.isObsEditing ? (
                <div className="odm-defeito-edit" style={{marginTop: "8px"}}>
                  <div className="odm-defeito-edit-row">
                    <div className="odm-defeito-field-wrap">
                      <textarea
                        className="odm-defeito-obs-textarea"
                        value={data.editObsValue || ''}
                        onChange={(e) => data.setEditObsValue?.(e.target.value)}
                        placeholder="Editar observação do defeito..."
                        disabled={data.isSavingDefeitoObs}
                      />
                    </div>
                    <div className="odm-defeito-actions">
                      <button
                        type="button"
                        className="odm-defeito-action-btn odm-defeito-action-btn--check"
                        onClick={() => data.handleSaveDefeitoObs?.()}
                        disabled={data.isSavingDefeitoObs}
                        title="Salvar observação"
                        aria-label="Salvar observação do defeito"
                      >
                        <Check size={14} strokeWidth={2} aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="odm-defeito-action-btn odm-defeito-action-btn--cancel"
                        onClick={() => data.handleCancelDefeitoObsEdit?.()}
                        disabled={data.isSavingDefeitoObs}
                        title="Cancelar"
                        aria-label="Cancelar edição da observação do defeito"
                      >
                        <X size={14} strokeWidth={2} aria-hidden />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{display: "flex", alignItems: "flex-start", gap: "6px", marginTop: "6px", minWidth: 0, width: "100%"}}>
                  {defeitoObsNode}
                  <OrderDetailsMockHoverTooltip text="Editar observação do defeito" placement="above">
                    <button
                      type="button"
                      onClick={() => data.handleStartObsEdit?.()}
                      style={{width: "18px", height: "18px", border: "none", background: "transparent", color: "var(--odm-icon)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "5px", cursor: "pointer", flexShrink: 0, marginTop: "1px"}}
                      aria-label="Editar observação do defeito"
                    >
                      <Pencil size={14} strokeWidth={2} aria-hidden />
                    </button>
                  </OrderDetailsMockHoverTooltip>
                </div>
              )}
              {data.defeitoSaveFeedback ? (
                <div style={{marginTop: "6px", fontSize: "11px", color: data.defeitoSaveFeedback.type === 'error' ? '#B91C1C' : '#15803D'}}>
                  {data.defeitoSaveFeedback.message}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={toggleMotivo}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "11px",
                fontWeight: 600,
                color: "var(--odm-text-secondary)",
                background: "var(--odm-surface-alt)",
                border: "none",
                borderRadius: "6px",
                padding: "4px 8px",
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {motivoToggleLabel}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"></path></svg>
            </button>
          </div>

          <div style={{maxHeight: motivoMaxHeight, opacity: motivoOpacity, overflow: "hidden", transition: "max-height .3s ease, opacity .25s ease, margin-top .3s ease", marginTop: motivoMarginTop}}>
            <div style={{borderTop: "1px solid var(--odm-border)", paddingTop: "12px", maxHeight: motivoPanelEditing ? "480px" : "220px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "10px"}}>
              <div style={{display: "flex", gap: "8px", alignItems: "center"}}>
                <span style={{fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--odm-muted)", width: "70px", flexShrink: 0, lineHeight: 1.35}}>Motivo</span>
                <div style={{flex: 1, minWidth: 0}}>
                  {motivoEditing ? (
                    <div className="odm-defeito-edit">
                      <div className="odm-defeito-edit-row">
                        <div className="odm-defeito-field-wrap">
                          <input
                            type="text"
                            className="odm-defeito-input"
                            value={data.motivoEditValue || ''}
                            onChange={onMotivoDraftChange}
                            maxLength={data.MOTIVO_OS_MAX}
                            placeholder="Sem motivo informado"
                            aria-label="Editar motivo"
                            autoFocus
                            disabled={data.isSavingMotivo}
                          />
                        </div>
                        <div className="odm-defeito-actions">
                          <button
                            type="button"
                            className="odm-defeito-action-btn odm-defeito-action-btn--check"
                            onClick={saveEditMotivo}
                            disabled={data.isSavingMotivo}
                            title="Salvar motivo"
                            aria-label="Salvar motivo"
                          >
                            <Check size={14} strokeWidth={2} aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="odm-defeito-action-btn odm-defeito-action-btn--cancel"
                            onClick={cancelEditMotivo}
                            disabled={data.isSavingMotivo}
                            title="Cancelar"
                            aria-label="Cancelar edição do motivo"
                          >
                            <X size={14} strokeWidth={2} aria-hidden />
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
                      <span style={{fontSize: "13px", color: "var(--odm-text-secondary)", lineHeight: 1.35}}>{data.motivoDisplay || 'Sem motivo informado'}</span>
                      <OrderDetailsMockHoverTooltip text="Editar motivo" placement="above">
                        <button type="button" onClick={startEditMotivo} style={{width: "18px", height: "18px", border: "none", background: "transparent", color: "var(--odm-icon)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "5px", cursor: "pointer"}} aria-label="Editar motivo">
                          <Pencil size={14} strokeWidth={2} aria-hidden />
                        </button>
                      </OrderDetailsMockHoverTooltip>
                    </div>
                  )}
                </div>
              </div>

              <div style={{display: "flex", gap: "8px", alignItems: "center"}}>
                <span style={{fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--odm-muted)", width: "70px", flexShrink: 0, lineHeight: 1.35}}>Obs. geral</span>
                <div style={{flex: 1, minWidth: 0}}>
                  {obsGeralEditing ? (
                    <div className="odm-defeito-edit">
                      <div className="odm-defeito-edit-row">
                        <div className="odm-defeito-field-wrap">
                          <input
                            type="text"
                            className="odm-defeito-input"
                            value={data.editObsGeralValue || ''}
                            onChange={(e) => data.setEditObsGeralValue?.(e.target.value)}
                            placeholder="Nenhuma observação geral."
                            aria-label="Editar observação geral"
                            autoFocus
                            disabled={data.isSavingObsGeral}
                          />
                        </div>
                        <div className="odm-defeito-actions">
                          <button
                            type="button"
                            className="odm-defeito-action-btn odm-defeito-action-btn--check"
                            onClick={() => data.handleSaveObsGeral?.()}
                            disabled={data.isSavingObsGeral}
                            title="Salvar observação geral"
                            aria-label="Salvar observação geral"
                          >
                            <Check size={14} strokeWidth={2} aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="odm-defeito-action-btn odm-defeito-action-btn--cancel"
                            onClick={() => data.handleCancelObsGeralEdit?.()}
                            disabled={data.isSavingObsGeral}
                            title="Cancelar"
                            aria-label="Cancelar edição da observação geral"
                          >
                            <X size={14} strokeWidth={2} aria-hidden />
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
                      <span style={{fontSize: "13px", color: "var(--odm-text-secondary)"}}>{data.obsGeralText || 'Nenhuma observação geral.'}</span>
                      <OrderDetailsMockHoverTooltip text="Editar observação geral" placement="above">
                        <button type="button" onClick={() => data.handleStartObsGeralEdit?.()} style={{width: "18px", height: "18px", border: "none", background: "transparent", color: "var(--odm-icon)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "5px", cursor: "pointer", flexShrink: 0}} aria-label="Editar observação geral">
                          <Pencil size={14} strokeWidth={2} aria-hidden />
                        </button>
                      </OrderDetailsMockHoverTooltip>
                    </div>
                  )}
                  {data.obsGeralSaveFeedback ? (
                    <div style={{marginTop: "6px", fontSize: "11px", color: data.obsGeralSaveFeedback.type === 'error' ? '#B91C1C' : '#15803D'}}>
                      {data.obsGeralSaveFeedback.message}
                    </div>
                  ) : null}
                </div>
              </div>

              <div>
                <button
                  type="button"
                  onClick={openSugestao}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    fontSize: "11px",
                    fontWeight: 600,
                    height: "28px",
                    boxSizing: "border-box",
                    padding: "0 9px",
                    borderRadius: "7px",
                    cursor: "pointer",
                    border: "1px solid var(--odm-border)",
                    background: "var(--odm-chip-bg)",
                    color: "var(--odm-ink)",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                  }}
                >
                  <i className="bi bi-lightbulb" aria-hidden style={{fontSize: "12px", lineHeight: 1}} />
                  Consultar solução
                </button>
              </div>
            </div>
          </div>
        </div>

        <div style={{background: "var(--odm-chip-bg)", border: "1px solid var(--odm-border)", borderRadius: "10px", padding: "14px 16px"}}>
          <div style={{display: "flex", alignItems: "center", gap: "7px", marginBottom: "12px"}}>
            <div style={{width: "20px", height: "20px", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--odm-title)", flexShrink: 0}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s7-6.5 7-11a7 7 0 1 0-14 0c0 4.5 7 11 7 11Z"></path><circle cx="12" cy="10" r="2.5"></circle></svg>
            </div>
            <span style={{fontSize: "13px", fontWeight: 700, color: "var(--odm-title)"}}>Localização e Contato</span>
          </div>

          <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px", marginBottom: "10px"}}>
            <div style={{minWidth: 0, flex: 1}}>
              <div style={{fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--odm-muted)", marginBottom: "3px"}}>Endereço</div>
              <div style={{fontSize: "13px", fontWeight: 700, color: "var(--odm-text)"}}>{data.streetAddress}</div>
              {addressLine2Display ? <div style={{fontSize: "11px", color: "var(--odm-muted)", marginTop: "1px"}}>{addressLine2Display}</div> : null}
              {data.addressEdit?.isLocalEditing ? (
                <div className="odm-defeito-edit" style={{marginTop: "8px"}}>
                  <div className="odm-defeito-edit-row">
                    <span className="odm-loc-edit-label">Local</span>
                    <div className="odm-defeito-field-wrap" style={{flex: 1}}>
                      <input
                        type="text"
                        className="odm-defeito-input"
                        value={data.addressEdit.localEditValue || ''}
                        onChange={(e) => data.addressEdit.setLocalEditValue?.(e.target.value)}
                        maxLength={data.addressEdit.LOCAL_MAX || 100}
                        placeholder="Ex.: Loja 5 — Setor atendimento"
                        aria-label="Editar localização"
                        autoFocus
                        disabled={data.addressEdit.isSavingLocal}
                      />
                    </div>
                    <div className="odm-defeito-actions">
                      <button
                        type="button"
                        className="odm-defeito-action-btn odm-defeito-action-btn--check"
                        onClick={() => data.addressEdit.saveLocal?.()}
                        disabled={data.addressEdit.isSavingLocal}
                        title="Salvar localização"
                        aria-label="Salvar localização"
                      >
                        <Check size={14} strokeWidth={2} aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="odm-defeito-action-btn odm-defeito-action-btn--cancel"
                        onClick={() => data.addressEdit.cancelLocalEdit?.()}
                        disabled={data.addressEdit.isSavingLocal}
                        title="Cancelar"
                        aria-label="Cancelar edição de localização"
                      >
                        <X size={14} strokeWidth={2} aria-hidden />
                      </button>
                    </div>
                  </div>
                  {data.addressEdit.localFeedback?.type === 'error' ? (
                    <div style={{fontSize: "11px", color: "#dc2626", marginTop: "4px"}}>{data.addressEdit.localFeedback.message}</div>
                  ) : null}
                </div>
              ) : (
                <div className="odm-loc-edit-row">
                  <span className="odm-loc-edit-label">Local</span>
                  <span className={`odm-loc-edit-value${data.addressEdit?.currentLocal ? '' : ' is-empty'}`}>
                    {data.addressEdit?.currentLocal || 'Não informado'}
                  </span>
                  <OrderDetailsMockHoverTooltip text="Alterar localização" placement="above">
                    <button
                      type="button"
                      className="odm-loc-edit-pencil"
                      onClick={() => data.addressEdit?.startLocalEdit?.()}
                      aria-label="Alterar localização"
                    >
                      <Pencil size={12} strokeWidth={2} aria-hidden />
                    </button>
                  </OrderDetailsMockHoverTooltip>
                </div>
              )}
              {data.addressEdit?.localFeedback?.type === 'success' && !data.addressEdit?.isLocalEditing ? (
                <div style={{fontSize: "11px", color: "#16a34a", marginTop: "4px"}}>{data.addressEdit.localFeedback.message}</div>
              ) : null}
              {data.addressEdit?.addressFeedback?.type === 'success' ? (
                <div style={{fontSize: "11px", color: "#16a34a", marginTop: "4px"}}>{data.addressEdit.addressFeedback.message}</div>
              ) : null}
            </div>
            <div className="odm-maps-actions">
              <OrderDetailsMockHoverTooltip text="Alterar endereço" placement="above">
                <button
                  type="button"
                  className="odm-addr-edit-btn"
                  onClick={() => data.addressEdit?.openAddressModal?.()}
                  aria-label="Alterar endereço"
                >
                  <Pencil size={14} strokeWidth={2} aria-hidden />
                </button>
              </OrderDetailsMockHoverTooltip>
              <button type="button" onClick={data.openGoogleMaps} className="odm-maps-btn">
                <Map size={16} strokeWidth={2} aria-hidden />
                Ver no Maps
              </button>
            </div>
          </div>

          <div style={{borderTop: "1px solid var(--odm-border)", paddingTop: "10px"}}>
            <div style={{fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--odm-muted)", marginBottom: "3px"}}>Solicitante & Contatos</div>
            <div style={{display: "flex", alignItems: "flex-start", gap: "6px", minWidth: 0}}>
              <div style={{flex: 1, minWidth: 0}}>
                <div style={{fontSize: "13px", fontWeight: 600, color: "var(--odm-text)"}}>
                  {data.solicitante}
                  {data.fone1Display ? ` · ${data.fone1Display}` : ''}
                </div>
                {data.solicitanteEmail ? (
                  <a href={`mailto:${data.solicitanteEmail}`} style={{fontSize: "12px", fontWeight: 600}}>{data.solicitanteEmail}</a>
                ) : null}
                {data.fone2Display || data.celularDisplay ? (
                  <div style={{fontSize: "12px", color: "var(--odm-muted)", marginTop: "4px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap"}}>
                    {data.fone2Display ? <span>Fone 2: {data.fone2Display}</span> : null}
                    {data.celularDisplay ? (
                      <span style={{display: "inline-flex", alignItems: "center", gap: "6px", lineHeight: 1}}>
                        <span style={{fontSize: "12px", lineHeight: 1, display: "inline-flex", alignItems: "center"}}>{data.celularDisplay}</span>
                        {data.celularWaHref ? (
                          <OrderDetailsMockHoverTooltip text="Abrir WhatsApp" placement="above">
                            <a
                              href={data.celularWaHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Abrir WhatsApp"
                              style={{display: "inline-flex", alignItems: "center", justifyContent: "center", width: "16px", height: "16px", color: "#25D366", lineHeight: 1, flexShrink: 0}}
                            >
                              <i className="bi bi-whatsapp" aria-hidden style={{fontSize: "15px", lineHeight: 1, display: "block"}} />
                            </a>
                          </OrderDetailsMockHoverTooltip>
                        ) : null}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <OrderDetailsMockHoverTooltip text="Editar solicitante e contatos" placement="above">
                <button
                  type="button"
                  onClick={() => data.solicitanteEdit?.openModal?.()}
                  style={{width: "18px", height: "18px", border: "none", background: "transparent", color: "var(--odm-icon)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "5px", cursor: "pointer", flexShrink: 0, marginTop: "1px"}}
                  aria-label="Editar solicitante e contatos"
                >
                  <Pencil size={14} strokeWidth={2} aria-hidden />
                </button>
              </OrderDetailsMockHoverTooltip>
            </div>
            {data.solicitanteEdit?.savedNote ? (
              <div style={{fontSize: "11px", color: "#15803D", marginTop: "4px"}}>{data.solicitanteEdit.savedNote}</div>
            ) : null}
          </div>
        </div>
      </div>
      ) : null}
    </div>
) : null}
    
    </div>

    {/* RIGHT RAIL PANEL */}
    <div style={{ width: `${panelWidth}px`, transition: "width .22s ease", overflow: "hidden", borderLeft: panelBorder, background: "var(--odm-surface-soft)", flexShrink: 0, alignSelf: "stretch", display: "flex", flexDirection: "column" }}>
      <div style={{ width: `${panelWidth}px`, minHeight: "100%", display: "flex", flexDirection: "column" }}>

        {isNotasOpen ? (

          <div style={{display: "flex", flexDirection: "column", height: "100%"}}>
            <div style={{padding: "14px 18px 0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px"}}>
              <span style={{fontSize: "13px", fontWeight: 700, color: "var(--odm-text)"}}>Anotações</span>
              <button type="button" onClick={closePanel} style={{width: "26px", height: "26px", border: "none", background: "transparent", color: "var(--odm-muted)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0}} aria-label="Fechar">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"></path><path d="M6 6l12 12"></path></svg>
              </button>
            </div>
            <div style={{flex: 1, overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: "12px"}}>
              {data.loadingNotes ? <div style={{fontSize: "12px", color: "var(--odm-muted)"}}>Carregando anotações…</div> : null}
              {data.notesError ? <div style={{fontSize: "12px", color: "#DC2626"}}>{data.notesError}</div> : null}
              {!data.loadingNotes && notes.length === 0 ? <div style={{fontSize: "12px", color: "var(--odm-muted)"}}>Nenhuma anotação ainda.</div> : null}
              {notes.map((n) => {
                const photoUrl =
                  n.profilePhotoUserId != null
                    ? notePhotoMap[String(n.profilePhotoUserId)] || null
                    : null;
                return (
                <div key={n.id} style={{display: "flex", gap: "10px"}}>
                  <div style={{width: "30px", height: "30px", borderRadius: "50%", background: "var(--odm-accent-soft)", color: "var(--odm-accent)", fontWeight: 700, fontSize: "11px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden"}}>
                    {photoUrl ? (
                      <img src={photoUrl} alt="" style={{width: "100%", height: "100%", objectFit: "cover", display: "block"}} />
                    ) : (
                      n.initials
                    )}
                  </div>
                  <div style={{minWidth: 0}}>
                    <div><span style={{fontSize: "12px", fontWeight: 700, color: "var(--odm-text)"}}>{n.name}</span> <span style={{fontSize: "11px", color: "var(--odm-muted)", marginLeft: "4px"}}>{n.date}</span></div>
                    <div style={{fontSize: "13px", color: "var(--odm-text-secondary)", marginTop: "3px"}}>{n.text}</div>
                  </div>
                </div>
                );
              })}
            </div>
            <div className="order-details-mock-notes-compose">
              <div className="order-details-mock-notes-input-wrap">
                <textarea
                  className="order-details-mock-notes-input"
                  value={noteInput}
                  onChange={onNoteInputChange}
                  placeholder="Digite uma anotação..."
                  rows={1}
                  disabled={Boolean(data.savingNote)}
                  aria-label="Digite uma anotação"
                />
                <button
                  type="button"
                  className="order-details-mock-notes-send"
                  onClick={sendNote}
                  disabled={sendDisabled}
                  aria-label={data.savingNote ? 'Enviando anotação' : 'Enviar anotação'}
                >
                  <ArrowUp size={15} strokeWidth={2.5} aria-hidden />
                </button>
              </div>
              {data.saveError ? <div style={{fontSize: "12px", color: "#DC2626", marginTop: "6px"}}>{data.saveError}</div> : null}
            </div>
          </div>
        
) : null}

        {isReincOpen ? (

          <div style={{display: "flex", flexDirection: "column", height: "100%"}}>
            <div style={{padding: "14px 18px 0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px"}}>
              <span style={{fontSize: "13px", fontWeight: 700, color: "var(--odm-text)"}}>Histórico de reincidência</span>
              <button type="button" onClick={closePanel} style={{width: "26px", height: "26px", border: "none", background: "transparent", color: "var(--odm-muted)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0}} aria-label="Fechar">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"></path><path d="M6 6l12 12"></path></svg>
              </button>
            </div>
            <div style={{flex: 1, overflowY: "auto", padding: "14px 18px 18px"}}>
              {reincLive.loading ? <div style={{fontSize: "12px", color: "var(--odm-muted)"}}>Carregando…</div> : null}
              {reincLive.error ? <div style={{fontSize: "12px", color: "#DC2626"}}>{reincLive.error}</div> : null}
              {!reincLive.loading && reincidencias.length === 0 ? <div style={{fontSize: "12px", color: "var(--odm-muted)"}}>Sem registros.</div> : null}
              <div style={{borderLeft: "2px solid var(--odm-border)", marginLeft: "4px", paddingLeft: "14px"}}>
                {reincidencias.map((h, hIdx) => (

                  <div key={h.os} style={{position: "relative", marginBottom: "11px"}}>
                    <span style={{position: "absolute", left: "-19px", top: "3px", width: "8px", height: "8px", borderRadius: "50%", background: hIdx === 0 ? "var(--odm-accent)" : (h.dotColor || "var(--odm-dot)"), border: "2px solid var(--odm-surface-soft)"}}></span>
                    <div style={{fontSize: "12px", fontWeight: 700, color: "var(--odm-text)"}}>OS {h.os}{h.tag ? <span style={{fontSize: "11px", fontWeight: 700, color: "var(--odm-accent)"}}>{` ${h.tag}`}</span> : null}</div>
                    <div style={{fontSize: "11px", color: "var(--odm-muted)"}}>{h.date}</div>
                  </div>
                
))}
              </div>
            </div>
          </div>
        
) : null}

        {isHistoricoOpen ? (

          <div style={{display: "flex", flexDirection: "column", height: "100%"}}>
            <div style={{padding: "10px 14px 0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px"}}>
              <span style={{fontSize: "13px", fontWeight: 700, color: "var(--odm-title)"}}>Histórico do processo</span>
              <button type="button" onClick={closePanel} style={{width: "26px", height: "26px", border: "none", background: "transparent", color: "var(--odm-muted)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0}} aria-label="Fechar">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"></path><path d="M6 6l12 12"></path></svg>
              </button>
            </div>
            <div style={{flex: 1, overflowY: "auto", padding: "12px 18px 20px"}}>
              {data.loadingHistorico ? <div style={{fontSize: "12px", color: "var(--odm-muted)"}}>Carregando histórico…</div> : null}
              {data.historicoError ? <div style={{fontSize: "12px", color: "#DC2626"}}>{data.historicoError}</div> : null}
              {!data.loadingHistorico && (data.historicoMapped || []).length === 0 ? <div style={{fontSize: "12px", color: "var(--odm-muted)"}}>Sem histórico de processo.</div> : null}
              {!data.loadingHistorico && data.historicoCycles?.cycles?.length ? (
                <OrderDetailsMockCiclos cycles={data.historicoCycles.cycles} steps={data.historicoMapped} originalFocus={originalCycleFocus} osNumber={displayOs} />
              ) : (
              <div style={{borderLeft: "2px solid var(--odm-border)", marginLeft: "4px", paddingLeft: "14px"}}>
                {(data.historicoMapped || []).map((step, stepIdx) => (

                  <div key={step.id} style={{position: "relative", marginBottom: "11px"}}>
                    <span style={{position: "absolute", left: "-19px", top: "3px", width: "8px", height: "8px", borderRadius: "50%", background: stepIdx === (data.historicoMapped || []).length - 1 ? "var(--odm-accent)" : "var(--odm-dot)", border: "2px solid var(--odm-surface-soft)"}}></span>
                    <div style={{fontSize: "12px", fontWeight: 700, color: "var(--odm-text)"}}>{step.label}</div>
                    <div style={{fontSize: "11px", color: "var(--odm-muted)"}}>{step.date}</div>
                    {step.observacoes ? (
                      <div style={{fontSize: "11px", color: "var(--odm-muted)", marginTop: "2px", lineHeight: 1.35}}>{step.observacoes}</div>
                    ) : null}
                  </div>
                
))}
              </div>
              )}
            </div>
          </div>
        
) : null}

        {isPedidoOpen ? (

          <div style={{display: "flex", flexDirection: "column", height: "100%"}}>
            <div style={{padding: "14px 18px 0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px"}}>
              <div style={{display: "flex", alignItems: "center", gap: "5px", minWidth: 0, flexWrap: "wrap"}}>
                <span style={{fontSize: "13px", fontWeight: 700, color: "var(--odm-text)", fontFamily: "'Geist Mono',monospace"}}>{selectedPedido.id}</span>
                <span aria-hidden style={{display: "inline-block", width: "4px", height: "4px", margin: "0 1px", borderRadius: "50%", background: "var(--odm-dot)", flexShrink: 0}} />
                <span style={{fontSize: "12px", fontWeight: 600, color: "var(--odm-muted)", fontFamily: "'Geist Mono',monospace", whiteSpace: "nowrap"}}>
                  {selectedPedido.notaFiscal ? `NF ${selectedPedido.notaFiscal}` : 'NF —'}
                </span>
              </div>
              <button type="button" onClick={closePanel} style={{width: "26px", height: "26px", border: "none", background: "transparent", color: "var(--odm-muted)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0}} aria-label="Fechar">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"></path><path d="M6 6l12 12"></path></svg>
              </button>
            </div>
            <div style={{flex: 1, display: "flex", overflow: "hidden"}}>
            <div style={{flex: 1, minWidth: 0, overflowY: "auto", padding: "14px 18px 18px"}}>
              {pedidoLive.loading ? <div style={{fontSize: "12px", color: "var(--odm-muted)", marginBottom: "12px"}}>Carregando pedido…</div> : null}
              {pedidoLive.error ? <div style={{fontSize: "12px", color: "#DC2626", marginBottom: "12px"}}>{pedidoLive.error}</div> : null}
              {selectedPedido.statusAtual ? (

                <div style={{marginBottom: "16px"}}>
                  <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px"}}>
                    <div style={{fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--odm-muted)"}}>Status Atual</div>
                    {selectedPedido.hasProcesso ? (

                      <button type="button" onClick={toggleProcesso} style={{display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: 600, color: "var(--odm-text-secondary)", background: "var(--odm-surface-alt)", border: "none", borderRadius: "6px", padding: "4px 8px", cursor: "pointer", whiteSpace: "nowrap"}}>
                        {processoToggleLabel}
                        <IconArrowBarToRightDashed size={14} stroke={1.8} aria-hidden />
                      </button>
                    
) : null}
                  </div>
                  <div style={{display: "flex", alignItems: "flex-start", gap: "8px"}}>
                    <span style={{display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: "2px", fontSize: "11px", fontWeight: 700, background: "var(--odm-accent-soft)", color: "var(--odm-accent)", padding: "4px 8px", borderRadius: "6px"}}>
                      <span>{selectedPedido.statusAtual.code} · {selectedPedido.statusAtual.label}</span>
                      <span style={{fontSize: "90%", fontWeight: 600, color: "var(--odm-accent)", lineHeight: 1.2}}>{selectedPedido.statusAtual.date}</span>
                    </span>
                  </div>
                </div>
              
) : null}

              {selectedPedido.hasProdutos ? (

                <div style={{marginBottom: "16px"}}>
                  <div style={{fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--odm-muted)", marginBottom: "8px"}}>Produtos</div>
                  <div style={{display: "flex", flexDirection: "column", gap: "6px"}}>
                    {selectedPedido.produtos.map((prod) => (

                      <div key={prod.key || prod.codigo} style={{background: "var(--odm-chip-bg)", border: "1px solid var(--odm-border)", borderRadius: "8px", padding: "9px 10px"}}>
                        <div style={{display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px"}}>
                          <span style={{display: "inline-flex", alignItems: "center", fontSize: "11px", fontWeight: 700, fontFamily: "'Geist Mono',monospace", color: "var(--odm-text)", flexShrink: 0, minWidth: 0}}>
                            <span>{prod.codigo}</span>
                            <span aria-hidden style={{display: "inline-block", width: "4px", height: "4px", margin: "0 5px", borderRadius: "50%", background: "var(--odm-dot)", verticalAlign: "middle", position: "relative", top: "-1px", flexShrink: 0}} />
                            <span style={{fontWeight: 600, color: "var(--odm-muted)"}}>{prod.referencia || '—'}</span>
                          </span>
                          <span style={{fontSize: "10px", fontWeight: 700, color: "var(--odm-accent)", flexShrink: 0}}>{prod.fabricante}</span>
                        </div>
                        <div style={{display: "flex", alignItems: "center", flexWrap: "wrap", fontSize: "12px", color: "var(--odm-text-secondary)", marginTop: "3px", lineHeight: 1.35}}>
                          <span>{prod.produto}</span>
                          <span aria-hidden style={{display: "inline-block", width: "4px", height: "4px", margin: "0 6px", borderRadius: "50%", background: "var(--odm-dot)", verticalAlign: "middle", position: "relative", top: "-1px", flexShrink: 0}} />
                          <span style={{fontSize: "11px", color: "var(--odm-muted)", whiteSpace: "nowrap", background: "var(--odm-surface-alt)", borderRadius: "5px", padding: "1px 6px"}}>
                            Qtde <b style={{color: "var(--odm-text)"}}>{prod.qtde}</b>
                          </span>
                          <span aria-hidden style={{display: "inline-block", width: "4px", height: "4px", margin: "0 6px", borderRadius: "50%", background: "var(--odm-dot)", verticalAlign: "middle", position: "relative", top: "-1px", flexShrink: 0}} />
                          <span style={{fontSize: "11px", color: "var(--odm-muted)", whiteSpace: "nowrap"}}>
                            Estoque <b style={{color: prod.edispColor}}>{prod.edisp}</b>
                          </span>
                        </div>
                      </div>
                    
))}
                  </div>
                </div>
              
) : null}

              {selectedPedido.observacoes ? (

                <div>
                  <div style={{fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--odm-muted)", marginBottom: "6px"}}>Observações</div>
                  <div style={{background: "var(--odm-chip-bg)", border: "1px solid var(--odm-border)", borderRadius: "8px", padding: "9px 10px", fontSize: "12px", color: "var(--odm-text-secondary)"}}>{selectedPedido.observacoes}</div>
                </div>
              
) : null}
            </div>
            {selectedPedido.hasProcesso ? (

              <div style={{width: processoPanelWidth, opacity: processoOpacity, overflow: "hidden", transition: "width .3s ease, opacity .25s ease", borderLeft: processoPanelBorder, flexShrink: 0}}>
                <div style={{width: "260px", height: "100%", overflowY: "auto", padding: "18px"}}>
                  <div style={{fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--odm-muted)", marginBottom: "8px"}}>Processo</div>
                  <div style={{borderLeft: "2px solid var(--odm-border)", marginLeft: "4px", paddingLeft: "14px"}}>
                    {selectedPedido.processo.map((step, stepIdx) => (
                      <div key={step.key || stepIdx} style={{position: "relative", marginBottom: "11px"}}>
                        <span style={{position: "absolute", left: "-19px", top: "3px", width: "8px", height: "8px", borderRadius: "50%", background: stepIdx === 0 ? "var(--odm-accent)" : "var(--odm-dot)", border: "2px solid var(--odm-surface-soft)"}}></span>
                        <div style={{fontSize: "12px", fontWeight: 700, color: "var(--odm-text)"}}>{step.label}</div>
                        <div style={{fontSize: "11px", color: "var(--odm-muted)"}}>{step.date}{step.author ? ` · ${step.author}` : ''}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            
) : null}
            </div>
          </div>
        
) : null}

        {isTecOpen ? (

          <div style={{display: "flex", flexDirection: "column", height: "100%"}}>
            <div style={{padding: "14px 18px 0", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px"}}>
              <span style={{fontSize: "13px", fontWeight: 700, color: "var(--odm-text)"}}>Técnicos do território</span>
              <button type="button" onClick={closePanel} style={{width: "26px", height: "26px", border: "none", background: "transparent", color: "var(--odm-muted)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0}} aria-label="Fechar">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"></path><path d="M6 6l12 12"></path></svg>
              </button>
            </div>
            <div style={{flex: 1, overflowY: "auto", padding: "10px 14px 14px", display: "flex", flexDirection: "column", gap: "4px"}}>
              {data.loadingTecnicosTerritorio ? <div style={{fontSize: "12px", color: "var(--odm-muted)"}}>Carregando técnicos…</div> : null}
              {data.tecnicosTerritorioError ? <div style={{fontSize: "12px", color: "#DC2626"}}>{data.tecnicosTerritorioError}</div> : null}
              {!data.loadingTecnicosTerritorio && technicians.length === 0 ? <div style={{fontSize: "12px", color: "var(--odm-muted)"}}>Nenhum técnico encontrado.</div> : null}
              {technicians.map((t) => (

                <div key={t} style={{display: "flex", alignItems: "center", gap: "7px", padding: "6px 8px", background: "var(--odm-chip-bg)", border: "1px solid var(--odm-border)", borderRadius: "6px", fontSize: "12px", fontWeight: 600, color: "var(--odm-text)", lineHeight: 1.25}}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--odm-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink: 0}}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                  {t}
                </div>
              
))}
            </div>
          </div>
        
) : null}
      </div>
    </div>
  </div>
</div>

      {data.atendimentosHistoricoMounted ? (
        <OpenOsV2EquipmentHistoryOverlay
          isOpen={data.atendimentosHistoricoOpen}
          onClose={data.closeAtendimentosHistorico}
          serie={order?.numeroSerie ?? order?.TB02115_NUMSERIE ?? ''}
          modelo={order?.equipamento ?? order?.TB02112_PRODUTO ?? ''}
          patrimonio={order?.patrimonio ?? order?.TB02112_PAT ?? ''}
          instalado={order?.dataInstalacao ?? order?.TB02112_DATA ?? null}
          zIndex={100050}
        />
      ) : null}

      <OrderDetailsSugestaoModal
        open={Boolean(data.showSugestaoSolucao)}
        onClose={() => data.setShowSugestaoSolucao?.(false)}
        orderId={order?.id ?? order?.TB02115_CODIGO}
        loadingDefeito={data.loadingDefeito}
        defeitoData={data.defeitoData}
      />

      <OrderDetailsEncaminharChamadoModal
        open={Boolean(encaminharPanel)}
        onClose={onEncaminharPanelClose}
        orderId={encaminharPanel?.orderId}
        areas={encaminharPanel?.areas ?? []}
        onConfirm={onEncaminharConfirm}
      />

      {anexoZoomOpen && selectedAnexo?.isImage && selectedAnexo?.url
        ? createPortal(
            <div
              className="order-details-mock-anexo-zoom-overlay"
              onClick={() => setAnexoZoomOpen(false)}
              role="presentation"
            >
              <div
                className="order-details-mock-anexo-zoom-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Anexo ampliado"
              >
                <button
                  type="button"
                  className="order-details-mock-anexo-zoom-close"
                  onClick={() => setAnexoZoomOpen(false)}
                  aria-label="Fechar"
                >
                  <X size={16} strokeWidth={2} aria-hidden />
                </button>
                <div className="order-details-mock-anexo-zoom-body">
                  <img
                    src={selectedAnexo.url}
                    alt={selectedAnexo.name || 'Anexo ampliado'}
                    className="order-details-mock-anexo-zoom-img"
                  />
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      {data.isDefeitoObsConfirmOpen
        ? createPortal(
            <div
              className="odm-defeito-obs-confirm-overlay"
              onClick={() => !data.isSavingDefeitoObs && data.handleCancelDefeitoObsConfirm?.()}
              role="presentation"
            >
              <div
                className="odm-defeito-obs-confirm-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="odm-defeito-obs-confirm-title"
              >
                <div className="odm-defeito-obs-confirm-header">
                  <h3 id="odm-defeito-obs-confirm-title" className="odm-defeito-obs-confirm-title">
                    Alterar observações do defeito?
                  </h3>
                  <button
                    type="button"
                    className="odm-defeito-obs-confirm-close"
                    onClick={() => data.handleCancelDefeitoObsConfirm?.()}
                    disabled={data.isSavingDefeitoObs}
                    aria-label="Fechar"
                  >
                    <X size={16} strokeWidth={2} aria-hidden />
                  </button>
                </div>
                <p className="odm-defeito-obs-confirm-desc">
                  O defeito será atualizado para{' '}
                  <strong>{data.selectedDefeito?.nome || 'o selecionado'}</strong>. Revise as
                  observações atuais abaixo — você pode editar antes de confirmar.
                </p>
                <textarea
                  className="odm-defeito-obs-confirm-textarea"
                  value={data.editObsValue || ''}
                  onChange={(e) => data.setEditObsValue?.(e.target.value)}
                  placeholder="Observações do defeito"
                  rows={5}
                  disabled={data.isSavingDefeitoObs}
                  autoFocus
                />
                <div className="odm-defeito-obs-confirm-actions">
                  <button
                    type="button"
                    className="user-form-link-btn user-form-footer-btn"
                    onClick={() => data.handleCancelDefeitoObsConfirm?.()}
                    disabled={data.isSavingDefeitoObs}
                  >
                    Voltar
                  </button>
                  <button
                    type="button"
                    className="user-form-profile-save-btn user-form-footer-btn"
                    onClick={() => data.handleConfirmDefeitoObsChange?.()}
                    disabled={data.isSavingDefeitoObs}
                  >
                    {data.isSavingDefeitoObs ? 'Salvando…' : 'Confirmar'}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      <OsReportV2PreviewModal
        open={impressao2Open}
        onClose={() => setImpressao2Open(false)}
        order={orderForImpressao2}
      />

      <OrderDetailsAddressEditModal
        open={Boolean(data.addressEdit?.addressModalOpen)}
        currentStreet={data.addressEdit?.currentStreet || data.streetAddress}
        currentLine2={formatAddressLine2Cep(data.addressEdit?.currentLine2 || '')}
        addrQuery={data.addressEdit?.addrQuery || ''}
        onQueryChange={(v) => data.addressEdit?.scheduleAddressSearch?.(v)}
        hits={data.addressEdit?.addrHits || []}
        searching={Boolean(data.addressEdit?.addrSearching)}
        canConfirm={Boolean(data.addressEdit?.canConfirmAddress)}
        isSaving={Boolean(data.addressEdit?.isSavingAddress)}
        feedback={data.addressEdit?.addressFeedback}
        onPick={(hit) => data.addressEdit?.pickAddressSuggestion?.(hit)}
        onConfirm={() => data.addressEdit?.confirmAddress?.()}
        onCancel={() => data.addressEdit?.closeAddressModal?.()}
      />
      <OrderDetailsSolicitanteEditModal edit={data.solicitanteEdit} />

    </>
  );
}
